import { z } from "zod";
import { dateSchema, firestoreTimestampSchema } from "./common.schema";
import {
  contractSignatureSnapshotSchema,
  doctorSignatureModeSchema,
} from "./signature.schema";

const nonEmptyString = z.string().trim().min(1, "Campo obbligatorio");

/** Italian VAT number (11 digits) with checksum validation. */
export function isValidItalianVatNumber(value: string): boolean {
  if (!/^\d{11}$/.test(value)) return false;

  const digits = value.split("").map(Number);
  let sum = 0;
  for (let index = 0; index < 10; index += 1) {
    if (index % 2 === 0) {
      sum += digits[index];
    } else {
      const doubled = digits[index] * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    }
  }

  return (10 - (sum % 10)) % 10 === digits[10];
}

function isRealIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

const generateCompanyContractInputObjectSchema = z.object({
  companyName: nonEmptyString,
  companyAddress: nonEmptyString,
  companyVatNumber: z
    .string()
    .trim()
    .refine(isValidItalianVatNumber, "Partita IVA non valida"),
  companyTaxCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^(?:\d{11}|[A-Z0-9]{16})$/, "Codice fiscale non valido"),
  signingDate: dateSchema.refine(isRealIsoDate, "Data non valida"),
  expiryDate: dateSchema.refine(isRealIsoDate, "Data non valida"),
  doctorName: nonEmptyString,
  doctorOfficeAddress: nonEmptyString,
  doctorProfessionalRegisterNumber: nonEmptyString,
});

export const generateCompanyContractInputSchema =
  generateCompanyContractInputObjectSchema
    .refine(
      (data) =>
        !/^\d{11}$/.test(data.companyTaxCode) ||
        isValidItalianVatNumber(data.companyTaxCode),
      {
        message: "Codice fiscale non valido",
        path: ["companyTaxCode"],
      },
    )
    .refine((data) => data.expiryDate > data.signingDate, {
      message: "La scadenza deve essere successiva alla stipula",
      path: ["expiryDate"],
    });

export type GenerateCompanyContractInput = z.infer<
  typeof generateCompanyContractInputSchema
>;

export const generateCompanyContractRequestSchema = z.object({
  input: generateCompanyContractInputSchema,
  doctorSignatureMode: doctorSignatureModeSchema.default("auto"),
});

export type GenerateCompanyContractRequest = z.infer<
  typeof generateCompanyContractRequestSchema
>;

export const companyContractSchema = z.object({
  storagePath: z.string().min(1),
  docxStoragePath: z.string().min(1).optional(),
  templatePath: z.string().min(1),
  templateVersion: z.string().min(1),
  generatedAt: firestoreTimestampSchema,
  expiresAt: firestoreTimestampSchema,
  generatedBy: z.string().min(1),
  inputSnapshot: generateCompanyContractInputSchema,
  signatureSnapshot: contractSignatureSnapshotSchema.optional(),
});

export type CompanyContract = z.infer<typeof companyContractSchema>;

export const companyContractDraftSchema =
  generateCompanyContractInputObjectSchema.partial().extend({
    companyId: z.string().min(1),
    primaryDoctorId: z.string().min(1),
    doctorSignatureAvailable: z.boolean().default(false),
    doctorSignatureUpdatedAt: firestoreTimestampSchema.optional(),
    missingFields: z.array(
      z.enum([
        "companyName",
        "companyAddress",
        "companyVatNumber",
        "companyTaxCode",
        "signingDate",
        "expiryDate",
        "doctorName",
        "doctorOfficeAddress",
        "doctorProfessionalRegisterNumber",
      ]),
    ),
  });

export type CompanyContractDraft = z.infer<typeof companyContractDraftSchema>;
